import { Outlet, useParams } from 'react-router-dom'

import {
  PerformanceReviewOverviewPageCompaniesLegalEntityFragment,
  PerformanceReviewOverviewPageCurrentEmployeeDocument,
  PerformanceReviewOverviewPageDocument,
  PerformanceReviewOverviewPageLocationsFragment,
  PerformanceReviewOverviewPageReviewProcessFragment,
  PerformanceReviewOverviewPageTeamsFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { FiltersProvider } from 'modules/performance_process/components/review/show/Filters/context'

export interface ReviewOverviewOutletContext {
  reviewProcess: PerformanceReviewOverviewPageReviewProcessFragment
  canOnlySeePersonalResults: boolean
  materializedTargetIds: number[]
  teams: PerformanceReviewOverviewPageTeamsFragment[]
  locations: PerformanceReviewOverviewPageLocationsFragment[]
  legalEntities: PerformanceReviewOverviewPageCompaniesLegalEntityFragment[]
}

const ReviewOverviewPage = () => {
  const { reviewId } = useParams()
  const { employee } = useCurrentSession()

  const { data, loading, error } = useQuery(PerformanceReviewOverviewPageDocument, {
    variables: {
      id: Number(reviewId),
      directManagedByCurrentEmployeeFilter: employee?.id
        ? {
            managerEmployeeId: employee.id,
            onlyDirectReports: true,
          }
        : undefined,
      managedByCurrentEmployeeFilter: employee?.id
        ? {
            managerEmployeeId: employee.id,
            onlyDirectReports: false,
          }
        : undefined,
    },
  })

  const {
    data: currentEmployeeData,
    loading: currentEmployeeLoading,
    error: currentEmployeeError,
  } = useQuery(PerformanceReviewOverviewPageCurrentEmployeeDocument, {
    variables: {
      id: Number(employee?.id),
    },
    skip: !employee,
  })

  const reviewProcess = data?.performance?.reviewProcess
  const currentEmployee = currentEmployeeData?.employees.employeeNamesConnection.nodes[0]
  const materializedTargetIds: number[] =
    data?.performance?.reviewProcess?.materializeTargets
      ?.map((target) => target?.employeeName?.id)
      .filter(Boolean) ?? []

  const teams = data?.teams?.teams ?? []
  const locations = data?.locations?.locations ?? []
  const legalEntities = data?.companies?.legalEntities ?? []

  const canOnlySeePersonalResults =
    !reviewProcess?._policies?.fullVisibility.can &&
    (employee ? reviewProcess?.reportsTargets?.totalCount ?? 0 : 0) === 0

  useEffect(() => {
    if (loading || !reviewProcess) return

    track(trackingEvents.performanceProcess.viewReview, {
      review_status: reviewProcess.status,
    })
  }, [loading])

  return (
    <ResponsivePage.Body
      loading={loading || currentEmployeeLoading}
      error={error || currentEmployeeError}
    >
      {reviewProcess && (
        <FiltersProvider reviewProcess={reviewProcess} currentEmployee={currentEmployee}>
          <Outlet
            context={{
              reviewProcess,
              canOnlySeePersonalResults,
              materializedTargetIds,
              teams,
              locations,
              legalEntities,
            }}
          />
        </FiltersProvider>
      )}
    </ResponsivePage.Body>
  )
}

export default ReviewOverviewPage
